import React, { useState, useEffect, useCallback } from 'react';
import { Alert, Card, Spinner, Badge } from 'react-bootstrap';
import ApiService from '../../helpers/apiService';
import SessionsTable from './SessionsTable';
import { ISession } from './types';

interface SessionsResponse {
  code: string;
  data: {
    items: ISession[];
  };
}

const SessionsPage: React.FC = () => {
  const [sessions, setSessions] = useState<ISession[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  const fetchSessions = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await ApiService.get<SessionsResponse>(
        '/session/v1/list'
      );
      const items = response?.data?.items ?? [];
      setSessions(items);
    } catch (err: any) {
      console.error('Ошибка при загрузке сессий:', err);
      setSessions([]);
      const msg =
        err?.response?.data?.message ?? err?.message ?? '';
      const isServiceUnavailable =
        err?.response?.status === 500 &&
        /refused|session service|rpc error/i.test(String(msg));
      setError(
        isServiceUnavailable
          ? 'Сервис сессий временно недоступен. Убедитесь, что session service запущен и доступен для admin-api.'
          : msg || 'Не удалось загрузить список сессий.'
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);

  const handleRevoke = async (sessionId: string) => {
    if (!window.confirm('Отозвать эту сессию?')) return;
    setActionLoading(true);
    try {
      await ApiService.delete('/session/v1/revoke', { id: sessionId });
      await fetchSessions();
    } catch (err: any) {
      console.error('Ошибка при отзыве сессии:', err);
      setError(
        err?.response?.data?.message ??
          err?.message ??
          'Не удалось отозвать сессию.'
      );
    } finally {
      setActionLoading(false);
    }
  };

  const handleRevokeAll = async (profileId: string) => {
    if (!window.confirm('Отозвать все сессии этого оператора?')) return;
    setActionLoading(true);
    try {
      await ApiService.delete('/session/v1/revoke-all', {
        profile_id: profileId
      });
      await fetchSessions();
    } catch (err: any) {
      console.error('Ошибка при отзыве всех сессий:', err);
      setError(
        err?.response?.data?.message ??
          err?.message ??
          'Не удалось отозвать сессии.'
      );
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <>
      <Card className="mb-3">
        <Card.Body>
          <h3 className="mb-0">Управление сессиями</h3>
          <p className="mt-2 mb-0">Просмотр и управление активными сессиями операторов</p>
        </Card.Body>
      </Card>

      {error && (
        <Alert variant="warning" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Card>
        <Card.Header className="d-flex justify-content-between align-items-center">
          <span>
            Активные сессии{' '}
            <Badge bg="secondary">{sessions.length}</Badge>
          </span>
          <button
            className="btn btn-sm btn-outline-primary"
            onClick={fetchSessions}
            disabled={loading}
          >
            {loading ? (
              <Spinner animation="border" size="sm" />
            ) : (
              'Обновить'
            )}
          </button>
        </Card.Header>
        <Card.Body className="p-0">
          <SessionsTable
            data={sessions}
            onRevoke={handleRevoke}
            onRevokeAll={handleRevokeAll}
            loading={loading || actionLoading}
          />
        </Card.Body>
      </Card>
    </>
  );
};

export default SessionsPage;
